/*
If any additional functionality is added to this file and the file length increases substantially,
remove this file as an exclusion in the sonar-project.properties file so it can be reevaluated
by SonarQube.
If only "duplicates" are returned by SonarQube despite any changes, the file can be re-added
to the sonar-project.properties file.
*/

import { Button, Card, Divider, H2, List, ListItem, MenuOverlay } from '@fs/zion-ui'
import React from 'react'
import { Menu } from '@fs/zion-icon'
import { css } from '@emotion/core'

type TrainingNavSubItem = {
  id: string
  primaryText: string
  secondaryText: string
  to: string
  title: string
  mdFile: string
}

type TrainingNavItem = {
  id: string
  primaryText?: string
  secondaryText?: string
  to?: string
  title?: string
  mdFile?: string
  heading?: string
  subItems?: TrainingNavSubItem[]
}

export const TRAINING_NAV_ITEMS: TrainingNavItem[] = [
  {
    id: 'intro',
    primaryText: 'Introduction to FS Places',
    secondaryText: 'Overview of the FS Places system',
    to: '#intro',
    title: 'Introduction to FS Places',
    mdFile: '2_Introduction_FS_Places',
  },
  {
    id: 'key-concepts',
    primaryText: 'Key Concepts in FS Places',
    secondaryText: 'Essential knowledge for working with places',
    to: '#key-concepts',
    title: 'Key Concepts in FS Places',
    mdFile: '3_Key_Concepts_FS_Places',
  },
  {
    id: 'searching',
    primaryText: 'Searching for Places',
    secondaryText: 'Find places effectively',
    to: '#searching',
    title: 'Searching for Places',
    mdFile: '4_Searching_for_Places',
  },
  {
    id: 'editing',
    primaryText: 'Editing Entries in FS Places',
    heading: 'Editing Entries in FS Places',
    to: '#editing',
    title: 'Editing Entries in FS Places',
    mdFile: '5_Editing_Entries_FS_Places',
    subItems: [
      {
        id: 'parent-place',
        primaryText: 'Parent Place',
        secondaryText: 'Working with hierarchical relationships',
        to: '#parent-place',
        title: 'Parent Place',
        mdFile: '5a_Parent_Place',
      },
      {
        id: 'place-type',
        primaryText: 'Place Type',
        secondaryText: 'Understanding place categories',
        to: '#place-type',
        title: 'Place Type',
        mdFile: '5b_Place_Type',
      },
      {
        id: 'native-language',
        primaryText: 'Native Language',
        secondaryText: 'Setting appropriate languages',
        to: '#native-language',
        title: 'Native Language',
        mdFile: '5c_Native_Language',
      },
      {
        id: 'certification',
        primaryText: 'Certification Status',
        secondaryText: 'Quality indicators for places',
        to: '#certification',
        title: 'Certification Status',
        mdFile: '5d_Certification_Status',
      },
      {
        id: 'historical-periods',
        primaryText: 'Historical Periods',
        secondaryText: 'Managing time-based information',
        to: '#historical-periods',
        title: 'Historical Periods',
        mdFile: '5e_Historical_Periods',
      },
      {
        id: 'start-end-year',
        primaryText: 'Start Year & End Year',
        secondaryText: 'Setting place existence timeframes',
        to: '#start-end-year',
        title: 'Start Year & End Year',
        mdFile: '5f_Start_End_Year',
      },
      {
        id: 'coordinates',
        primaryText: 'Coordinates',
        secondaryText: 'Setting geographical location',
        to: '#coordinates',
        title: 'Coordinates',
        mdFile: '5g_Coordinates',
      },
      {
        id: 'names',
        primaryText: 'Names',
        secondaryText: 'Managing multiple place names',
        to: '#names',
        title: 'Names',
        mdFile: '5h_Names',
      },
      {
        id: 'research-links',
        primaryText: 'Research Links',
        secondaryText: 'Adding helpful resources',
        to: '#research-links',
        title: 'Research Links',
        mdFile: '5i_Research_Links',
      },
      {
        id: 'citations',
        primaryText: 'Citations',
        secondaryText: 'Documenting your sources',
        to: '#citations',
        title: 'Citations',
        mdFile: '5j_Citations',
      },
      {
        id: 'historical-info',
        primaryText: 'Historical Information',
        secondaryText: 'Adding contextual details',
        to: '#historical-info',
        title: 'Historical Information',
        mdFile: '5k_Historical_Information',
      },
      {
        id: 'related-places',
        primaryText: 'Related Places',
        secondaryText: 'Connecting associated locations',
        to: '#related-places',
        title: 'Related Places',
        mdFile: '5l_Related_Places',
      },
    ],
  },
  {
    id: 'creating-entries',
    primaryText: 'Creating New Entries in FS Places',
    secondaryText: 'Adding new places to the database',
    to: '#creating-entries',
    title: 'Creating New Entries in FS Places',
    mdFile: '6_Creating_New_Entries_FS_Places',
  },
]

// ----- Navigation rendering components -----

function TrainingNavListItems() {
  return (
    <>
      {TRAINING_NAV_ITEMS.map((item, index) => (
        <React.Fragment key={item.id}>
          {item.subItems && index !== 0 && <Divider marginY="sm" />}

          <ListItem
            dense
            to={item.to || `#${item.id}`}
            primaryText={item.primaryText || item.title}
            secondaryText={item.secondaryText}
          />

          {item.subItems?.map((subItem) => (
            <ListItem
              key={subItem.id}
              dense
              to={subItem.to || `#${subItem.id}`}
              primaryText={subItem.primaryText}
              secondaryText={subItem.secondaryText}
            />
          ))}
        </React.Fragment>
      ))}
    </>
  )
}

// ----- Side nav panel -----

export default function PlaceEditorTrainingNav(): JSX.Element {
  return (
    <div
      id="place-editor-training-nav"
      css={css`
        display: flex;
        flex-direction: column;
        height: calc(100vh - 160px);
        overflow-y: auto;
      `}
    >
      <div
        css={css`
          padding-bottom: 16px;
        `}
      >
        <Card margins="md">
          <H2 size="H5">Training Topics</H2>
          <Divider marginY="xs" />
          <List>
            <TrainingNavListItems />
          </List>
        </Card>
      </div>
    </div>
  )
}

// ----- Mobile menu dropdown -----

export function PlaceEditorNavMenu(): JSX.Element {
  return (
    <MenuOverlay menuButton={<Button Icon={Menu}>Open Training Menu</Button>} inline={false}>
      <TrainingNavListItems />
    </MenuOverlay>
  )
}
